/**
 * Runtime markers (runtime-handoff-v5, directive §17).
 *
 * markers.json is the single source for spawns, GK spots, cameras, tunnel exit,
 * fireworks and effect anchors. Integration code asks this module by name — it
 * never hard-codes a position that exists in the data.
 */
import { Vector3, Quaternion } from 'three';

export interface StadiumMarker {
  name: string;
  position: Vector3;
  rotation: Quaternion;
  scale: Vector3;
}

export interface MarkerSet {
  all: StadiumMarker[];
  byName: Map<string, StadiumMarker>;
}

export function parseMarkers(json: unknown): MarkerSet {
  const root = json as { markers?: Array<{ name: string; position: number[]; rotation?: number[]; scale?: number[] }> };
  const all: StadiumMarker[] = [];
  const byName = new Map<string, StadiumMarker>();
  for (const m of root.markers ?? []) {
    const p = m.position ?? [0, 0, 0];
    const r = m.rotation ?? [0, 0, 0, 1];
    const s = m.scale ?? [1, 1, 1];
    const marker: StadiumMarker = {
      name: m.name,
      position: new Vector3(p[0], p[1], p[2]),
      rotation: new Quaternion(r[0], r[1], r[2], r[3]),
      scale: new Vector3(s[0], s[1], s[2]),
    };
    all.push(marker);
    byName.set(m.name, marker);
  }
  // Fail loud if a gameplay-critical marker is missing.
  for (const need of ['MARKER_PitchCenter', 'MARKER_HomeSpawn_01', 'MARKER_AwaySpawn_01', 'MARKER_TunnelExit']) {
    if (!byName.has(need)) throw new Error(`markers: required marker "${need}" is missing`);
  }
  return { all, byName };
}

export function marker(set: MarkerSet, name: string): StadiumMarker {
  const m = set.byName.get(name);
  if (!m) throw new Error(`markers: no marker named "${name}"`);
  return m;
}

/** All markers whose name starts with a prefix (e.g. MARKER_Fireworks_ / MARKER_Camera). */
export function markersWithPrefix(set: MarkerSet, prefix: string): StadiumMarker[] {
  return set.all.filter((m) => m.name.startsWith(prefix));
}
